import { usePackages } from "@/hooks/usePackages";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { usePreviewFilterStore } from "@/stores/previewFilterStore";
import { useCurrencyStore } from "@/stores/currencyStore";
import { formatCurrency } from "@/utils/currencyFormatter";
import { Package } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { PackageWithRelations } from "@/features/packages/types";

interface PreviewPackageLegendProps {
  providerId: number;
  versionId: number;
  regionId?: number;
}

export function PreviewPackageLegend({ providerId, versionId, regionId }: PreviewPackageLegendProps) {
  const { packages, isLoading } = usePackages(providerId, versionId, regionId || 0);
  const {
    showPackageDescription,
    showPackageMonthlyPrice,
    showPackageYearlyPrice,
    showPackageType
  } = usePreviewFilterStore();
  const providerCurrency = useCurrencyStore.getState().getProviderCurrency(providerId);

  if (isLoading || !packages || packages.length === 0) {
    return null;
  }

  // Separate regular packages from add-ons
  const regularPackages = packages.filter((pkg: PackageWithRelations) => !pkg.is_addon);
  const addonPackages = packages.filter((pkg: PackageWithRelations) => pkg.is_addon);

  const renderPackageSection = (pkgs: PackageWithRelations[], title: string) => {
    if (pkgs.length === 0) return null;

    return (
      <div className="mb-4">
        <h5 className="text-sm font-medium mb-2 text-muted-foreground">{title}</h5>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Package Name</TableHead>
              {showPackageType && <TableHead>Type</TableHead>}
              {showPackageMonthlyPrice && <TableHead>Monthly Price</TableHead>}
              {showPackageYearlyPrice && <TableHead>Yearly Price</TableHead>}
              {showPackageDescription && <TableHead>Description</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pkgs.map((pkg) => (
              <TableRow key={pkg.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    {pkg.name}
                    {pkg.is_featured && (
                      <Badge variant="secondary" className="ml-2">Featured</Badge>
                    )}
                  </div>
                </TableCell>
                {showPackageType && (
                  <TableCell className="capitalize">
                    {pkg.package_type?.replace('_', ' ') || '-'}
                  </TableCell>
                )}
                {showPackageMonthlyPrice && (
                  <TableCell>
                    {pkg.monthly_price ? formatCurrency(pkg.monthly_price, providerCurrency) : '-'}
                  </TableCell>
                )}
                {showPackageYearlyPrice && (
                  <TableCell>
                    {pkg.yearly_price ? formatCurrency(pkg.yearly_price, providerCurrency) : '-'}
                  </TableCell>
                )}
                {showPackageDescription && (
                  <TableCell className="text-muted-foreground">
                    {pkg.description || '-'}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="mb-6 border rounded-md p-4">
      <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
        <Package className="h-4 w-4" />
        Packages
      </h4>
      {renderPackageSection(regularPackages, 'Regular Packages')}
      {renderPackageSection(addonPackages, 'Package Add-ons')}
    </div>
  );
}
